import { UpperCasePipe } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { ChangedSimpleOrderInterface } from '@modules/orders-module/interfaces/changed-simple-order.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { SimpleOrderFormInterface } from '@modules/orders-module/interfaces/simple-order-form.interface'
import { TitleOrderTypePipe } from '@modules/orders-module/pipes/title-order-type.pipe'
import { AlpacaAccountInterface } from '@modules/alpaca-module/alpaca-account/interfaces/alpaca-account.interface'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TuiIcon, TuiTextfield } from '@taiga-ui/core'
import { finalize, Subject, takeUntil } from 'rxjs'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { PercentPipe } from '@shared/pipes/percent.pipe'
import { AlertService } from '@shared/services/alert.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'
import { CreateOrderInterface } from '@modules/orders-module/interfaces/create-order.interface'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { OrderPriceInputComponent } from '@shared/components/trading/orders/common/order-price-input/order-price-input.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-change-simple-order',
	templateUrl: './change-simple-order.component.html',
	styleUrl: './change-simple-order.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslatePipe,
		ReactiveFormsModule,
		TuiTextfield,
		TuiIcon,
		UpperCasePipe,
		HighlightDirective,
		TitleOrderTypePipe,
		PricePipe,
		QuantityPipe,
		PercentPipe,
		ButtonComponent,
		OrderPriceInputComponent,
	],
})
export class ChangeSimpleOrderComponent implements OnInit, OnDestroy {
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum

	public order: InputSignal<OrderInterface | null> = input<OrderInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _ordersStocksService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)

	protected alpacaAccount: WritableSignal<AlpacaAccountInterface | null> = signal<AlpacaAccountInterface | null>(null)
	protected approximateSum: WritableSignal<number> = signal<number>(0)
	protected isLoading: WritableSignal<boolean> = signal<boolean>(false)
	protected form: FormGroup | null = null

	protected isStopPriceVisible: Signal<boolean> = computed<boolean>((): boolean => {
		const order: OrderInterface | null = this.order()

		return !!order && this._ordersStocksHelperService.checkIsStopPriceVisible(order.type)
	})

	protected isLimitPriceVisible: Signal<boolean> = computed<boolean>((): boolean => {
		const order: OrderInterface | null = this.order()

		return !!order && this._ordersStocksHelperService.checkIsLimitPriceVisible(order.type, order.order_class)
	})

	protected isTrailingStopVisible: Signal<boolean> = computed<boolean>((): boolean => {
		return this._ordersStocksHelperService.checkIsTrailingStopVisible(this.order()?.type)
	})

	protected trailIsPercent: Signal<boolean> = computed<boolean>((): boolean => {
		const order: OrderInterface | null = this.order()

		return !!order && !!order.trail_percent && !order.trail_price
	})
	private _listenAlpacaAccount(): void {
		this._alpacaAccountHelperService.alpacaAccount$
			.pipe(takeUntil(this._destroy$))
			.subscribe((alpacaAccount: AlpacaAccountInterface | null): void => {
				this.alpacaAccount.set(alpacaAccount)
			})
	}

	private _initForm(): void {
		this.form = this._formBuilder.group({
			limit_price: [null],
			stop_price: [null],
			trail: [null],
		})
	}

	private _fillForm(): void {
		const order: OrderInterface | null = this.order()
		const trailValue: string = order?.trail_percent || order?.trail_price || ''

		this.form?.patchValue({
			limit_price: order?.limit_price,
			stop_price: order?.stop_price,
			trail: trailValue ? parseFloat(trailValue) : null,
		})

		this._updateApproximateSum()
	}

	private _listenFormControlsChanges(): void {
		this.form?.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((): void => {
			this._updateApproximateSum()
		})
	}

	private _updateApproximateSum(): void {
		const order: OrderInterface | null = this.order()
		if (!order) return

		const formValue: CreateOrderInterface | undefined = this.form?.value
		const orderForCalculation: OrderInterface = {
			...order,
			limit_price: formValue?.limit_price ?? 0,
			stop_price: formValue?.stop_price ?? 0,
		}

		this.approximateSum.set(this._ordersStocksHelperService.getOrderPrice(orderForCalculation))
	}

	private _prepareSimpleOrderToChange(
		id: string,
		limit_price: number,
		stop_price: number,
		trail: number | null,
	): ChangedSimpleOrderInterface | null {
		return {
			id,
			...(this.isLimitPriceVisible() && limit_price != null && { limit_price }),
			...(this.isStopPriceVisible() && stop_price != null && { stop_price }),
			...(trail != null && { trail: trail.toString() }),
		}
	}

	protected onSubmit(): void {
		const orderId: string | undefined = this.order()?.id
		if (this.form?.invalid || !orderId) return

		const simpleOrderForm: SimpleOrderFormInterface | undefined = this.form?.value
		if (!simpleOrderForm) return

		const order: ChangedSimpleOrderInterface | null = this._prepareSimpleOrderToChange(
			orderId,
			simpleOrderForm.limit_price,
			simpleOrderForm.stop_price,
			simpleOrderForm.trail,
		)
		if (!order) return

		this.isLoading.set(true)
		this._ordersStocksService
			.changeSimpleOrder(order)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoading.set(false)),
			)
			.subscribe(
				(): void => {
					this._alertService.success(this._translateService.instant('orders.orderSuccessfullyChanged'))
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant("orders.couldn'tChangeOrder"),
					)
				},
			)
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._initForm()
		this._listenFormControlsChanges()
		this._fillForm()
		this._listenAlpacaAccount()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
